import { FC } from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { useRemediationFigure } from '../api/remediation-api';

/**
 * One image embedded in a remediated document.
 *
 * The AI writes diagrams to the pipeline bucket and puts the storage key on the
 * block — not a URL — so the browser cannot load it directly. This points at the
 * run-scoped figure endpoint, which is what turns that key into bytes a page can
 * show.
 *
 * A figure that will not load falls back to the same labelled box the generated
 * PDF uses, rather than a broken-image glyph: the reviewer needs to know
 * something belongs there, and the block's own caption is the best description
 * of it available.
 */
export const RemediationFigure: FC<{
  runId: string;
  pngKey: string | null | undefined;
  /** The block's text, used as the alt text and as the placeholder caption. */
  caption?: string | null;
  width?: number | null;
  height?: number | null;
}> = observer(({ runId, pngKey, caption, width, height }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { url, isLoading, isError } = useRemediationFigure(runId, pngKey);

  const label = caption?.trim() || t('app.remediation.review.figureFallback');

  if (pngKey && isLoading) {
    // Sized from the block's own intrinsic dimensions where it has them, so the
    // surrounding text does not jump once the image lands.
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={height && width ? undefined : 180}
        sx={{ my: 1.5, aspectRatio: width && height ? `${width} / ${height}` : undefined }}
      />
    );
  }

  if (!pngKey || isError || !url) {
    return (
      <Box
        sx={{
          border: `1px dashed ${theme.palette.grey[400]}`,
          borderRadius: 1,
          color: 'text.tertiary',
          fontStyle: 'italic',
          px: 1.5,
          py: 1.5,
          my: 1,
        }}
      >
        <Typography variant="textXS_regular">{label}</Typography>
      </Box>
    );
  }

  return (
    <Box component="figure" sx={{ m: 0, my: 1.5 }}>
      <Box
        component="img"
        src={url}
        alt={label}
        // The intrinsic size is carried on the block so the surrounding text
        // does not reflow once the bytes arrive.
        width={width ?? undefined}
        height={height ?? undefined}
        sx={{
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: 1,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      />
      {caption?.trim() && (
        <Typography
          component="figcaption"
          variant="textXS_regular"
          sx={{ color: 'text.tertiary', mt: 0.5, display: 'block' }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
});
